/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Table, Dropdown, MenuProps, Modal, Form, Input } from 'antd';
import { IconEdit, ExclamationIcon } from 'components/Elements/CustomSVGIcon';
import { MenuInfo } from 'rc-menu/lib/interface';
import Checkbox from 'components/Checkbox';
import Spinner from 'components/Spinner';
import Main from '../../components/layout/Main';
import { CombinedStates, AppDispatch } from '../../core/types';
import { getEmergencyServices, createEmergencyService, deleteEmergencyService } from '../../core/settings/settingsApi';
import { EmergencyService, CreateEmergencyServicePayload } from '../../core/settings/types';
import './settings.scss';

const Settings: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { emergencyServices, isFetching, isCreating, isDeleting, success } = useSelector((state: CombinedStates) => state.settings);
    const { userData } = useSelector((state: CombinedStates) => state.user);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [serviceToDelete, setServiceToDelete] = useState<EmergencyService | null>(null);
    const [form] = Form.useForm();
    const [selectedStages, setSelectedStages] = useState<string[]>([]);
    const [buttonLabels, setButtonLabels] = useState<{ [key: string]: string }>({});

    const availableStages = [
        { id: 1, name: 'Next Steps' },
        { id: 2, name: 'Call Emergency Services' },
        { id: 3, name: 'F.A.S.T Test T' },
    ];

    useEffect(() => {
        dispatch(getEmergencyServices());
    }, [dispatch]);

    useEffect(() => {
        if (success && !isAddModalVisible) {
            dispatch(getEmergencyServices());
        }
    }, [success, isAddModalVisible, dispatch]);

    const handleMenuClick = (e: MenuInfo, record: EmergencyService) => {
        if (e.key === 'edit') {
            console.log('edit');
        } else if (e.key === 'delete') {
            setServiceToDelete(record);
            setIsDeleteModalVisible(true);
        }
    };

    const getMenuItems = (record: EmergencyService): MenuProps['items'] => [
        {
            key: 'delete',
            label: 'Delete',
            onClick: () => handleMenuClick({ key: 'delete' } as MenuInfo, record),
            type: 'item',
        },
    ];

    const columns = [
        {
            title: 'Service Name',
            dataIndex: 'serviceName',
            key: 'serviceName',
            width: '25%',
        },
        {
            title: 'Telephone Number',
            dataIndex: 'telephoneNumber',
            key: 'telephoneNumber',
            width: '20%',
        },
        {
            title: 'Stage in ISTUMBLE Process',
            dataIndex: 'stageInProcess',
            key: 'stageInProcess',
            width: '45%',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '10%',
            render: (text: string, record: EmergencyService) => (
                <Dropdown
                    menu={{ items: getMenuItems(record) || [] }}
                    trigger={['click']}
                    placement="bottomRight"
                    className="custom-dropdown">
                    <Button type="link" icon={<IconEdit />} />
                </Dropdown>
            ),
        },
    ];

    const handleAddEmergencyService = () => {
        form.resetFields();
        setSelectedStages([]);
        setButtonLabels({});
        setIsAddModalVisible(true);
    };

    const handleDelete = async () => {
        if (serviceToDelete) {
            await dispatch(deleteEmergencyService({ id: serviceToDelete.id }));
            await dispatch(getEmergencyServices());
            setIsDeleteModalVisible(false);
            setServiceToDelete(null);
        }
    };

    const handleStageToggle = (stageName: string) => {
        setSelectedStages(prev => (prev.includes(stageName)
            ? prev.filter(s => s !== stageName)
            : [...prev, stageName]));
    };

    const handleButtonLabelChange = (stageName: string, value: string) => {
        setButtonLabels(prev => ({
            ...prev,
            [stageName]: value,
        }));
    };

    const handleAddSave = async () => {
        try {
            const values = await form.validateFields();

            const stageMap: { [key: string]: number } = {
                'Next Steps': 1,
                'Call Emergency Services': 2,
                'F.A.S.T Test T': 3,
            };

            const selectedStageIds = selectedStages
                .map(stageName => stageMap[stageName])
                .filter((id): id is number => id !== undefined);

            const apiButtonLabels: { [key: number]: string } = {};
            selectedStages.forEach(stageName => {
                const stageId = stageMap[stageName];
                const buttonLabel = buttonLabels[stageName];
                if (stageId && buttonLabel) {
                    apiButtonLabels[stageId] = buttonLabel;
                }
            });

            const payload: CreateEmergencyServicePayload = {
                serviceName: values.serviceName,
                telephoneNumber: values.telephoneNumber,
                selectedStages: selectedStageIds,
                ...(Object.keys(apiButtonLabels).length > 0 && { buttonLabels: apiButtonLabels }),
            };
            await dispatch(createEmergencyService(payload));
            setIsAddModalVisible(false);
            form.resetFields();
            setSelectedStages([]);
            setButtonLabels({});
        } catch (err) {
            console.error('Save error:', err);
        }
    };

    const handleAddCancel = () => {
        setIsAddModalVisible(false);
        form.resetFields();
        setSelectedStages([]);
        setButtonLabels({});
    };

    const getSubscriptionInfo = () => {
        if (!userData?.zone?.expiry_date) {
            return {
                expiryDate: 'N/A',
                daysRemaining: 0,
                isExpired: true,
            };
        }

        const expiryDate = new Date(userData.zone.expiry_date);
        const today = new Date();
        const timeDiff = expiryDate.getTime() - today.getTime();
        const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));

        return {
            expiryDate: expiryDate.toLocaleDateString('en-GB'),
            daysRemaining: Math.max(0, daysRemaining),
            isExpired: daysRemaining <= 0,
        };
    };

    const subscriptionInfo = getSubscriptionInfo();

    return (
        <Main
            className="settings"
            title={<div>Settings</div>}
            headerTitle={<div>Settings</div>}
            floatingHeader={false}
            footerContent={<div style={{ lineHeight: '30px', fontSize: '16px', float: 'right' }}>&copy; copyright 2024</div>}
            backLink={<Button type="link">Back</Button>}
            menuPlacement="left"
            menuWidth={0}>
            {isFetching ? (
                [<Spinner key="spinner" />]
            ) : (
                [
                    <h3 key="header" className="header">Subscription Status</h3>,
                    <div key="subscription" className="subscription-status">
                        <p className="subscription-text">
                            Your subscription will expire on {subscriptionInfo.expiryDate}. You have {subscriptionInfo.daysRemaining} days until you need to renew your subscription.
                        </p>
                    </div>,
                    <div key="emergency-services" className="emergency-services-section">
                        <div className="section-header">
                            <h3 className="section-title">Emergency Services</h3>
                            <Button
                                type="primary"
                                style={{ fontSize: '18px', padding: '20px 30px' }}
                                onClick={handleAddEmergencyService}
                                loading={isCreating}>
                                + Add Emergency Service
                            </Button>
                        </div>

                        <div className="emergency-services-container">
                            <div className="table-container">
                                <Table
                                    className="table-emergency-services"
                                    dataSource={emergencyServices}
                                    columns={columns}
                                    rowKey="id"
                                    rowClassName={(record, index) => (index % 2 === 0 ? 'light-row' : '')}
                                    pagination={{
                                        pageSize: 10,
                                        showTotal: (total, [from, to]) => `${from} - ${to} of ${total}`,
                                        showSizeChanger: false,
                                    }} />
                            </div>
                        </div>
                    </div>,
                    <Modal
                        key="add-modal"
                        title="Add Emergency Service"
                        open={isAddModalVisible}
                        footer={null}
                        onCancel={handleAddCancel}
                        width={700}
                        className="add-emergency-service-modal">
                        <Form form={form} layout="vertical">
                            <Form.Item
                                name="serviceName"
                                label="Service Name"
                                rules={[{ required: true, message: 'Please enter service name' }]}>
                                <Input placeholder="" />
                            </Form.Item>

                            <Form.Item
                                name="telephoneNumber"
                                label="Telephone Number"
                                rules={[
                                    { required: true, message: 'Please enter telephone number' },
                                    {
                                        pattern: /^[+]?[(]?[0-9\s\-()]{6,20}$/,
                                        message: 'Please enter a valid telephone number (6-20 characters, numbers, spaces, dashes, parentheses allowed)',
                                    },
                                    {
                                        min: 6,
                                        message: 'Telephone number must be at least 6 characters',
                                    },
                                    {
                                        max: 20,
                                        message: 'Telephone number cannot exceed 20 characters',
                                    },
                                ]}>
                                <Input placeholder="" />
                            </Form.Item>
                            <div className="divider" />
                            <div className="stages-section">
                                <h4>Stage in ISTUMBLE Process</h4>
                                <p className="stages-description">
                                    You can select multiple steps in the ISTUMBLE process to display this emergency service contact number.
                                </p>

                                <div className="stages-content">
                                    <div className="checkbox-section">
                                        <div className="checkbox-group">
                                            {availableStages.map((stage) => (
                                                <div key={stage.id}>
                                                    <div className="checkbox-item">
                                                        <Checkbox
                                                            checked={selectedStages.includes(stage.name)}
                                                            onChange={() => handleStageToggle(stage.name)} />
                                                        <span className="checkbox-label">{stage.name}</span>
                                                    </div>
                                                    {selectedStages.includes(stage.name) && (stage.name === 'Call Emergency Services' || stage.name === 'F.A.S.T Test T') && (
                                                        <div className="button-label-input">
                                                            <label>Button Label</label>
                                                            <Input
                                                                placeholder=""
                                                                value={buttonLabels[stage.name] || ''}
                                                                onChange={(e) => handleButtonLabelChange(stage.name, e.target.value)} />
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="disclaimer-section">
                                        <div className="info-icon">
                                            <ExclamationIcon />
                                        </div>
                                        <div className="disclaimer-content">
                                            <p>
                                                <strong>The Button Label field</strong> allows you to give a title to the button you are assigning the Emergency Service Number to. You can label each button differently for each stage.
                                            </p>
                                            <p>
                                                The button will be labelled as the default label &quot;Call Emergency Services&quot; if you do not update this field.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-buttons">
                                <Button
                                    type="primary"
                                    onClick={handleAddSave}
                                    className="save-button"
                                    loading={isCreating}>
                                    Save
                                </Button>
                                <Button
                                    onClick={handleAddCancel}
                                    className="cancel-button">
                                    Cancel
                                </Button>
                            </div>
                        </Form>
                    </Modal>,
                    <Modal
                        key="delete-modal"
                        title="Delete Emergency Service"
                        open={isDeleteModalVisible}
                        footer={[
                            <Button
                                key="delete"
                                type="primary"
                                danger
                                onClick={handleDelete}
                                loading={isDeleting}
                                style={{
                                    width: '230px',
                                    borderRadius: '11px',
                                    height: '45px',
                                    backgroundColor: '#0079C1',
                                }}>
                                Delete
                            </Button>,
                            <Button
                                key="cancel"
                                onClick={() => setIsDeleteModalVisible(false)}
                                style={{
                                    width: '230px',
                                    borderRadius: '11px',
                                    height: '45px',
                                    borderColor: '#0079C1',
                                    color: '#0079C1',
                                }}>
                                Cancel
                            </Button>,
                        ]}
                        onCancel={() => setIsDeleteModalVisible(false)}>
                        <p>
                            All record of this Emergency Service will be permanently deleted. Are you sure you want to delete this service?
                        </p>
                    </Modal>,
                ]
            )}
        </Main>
    );
};

export default Settings;
